// components/imageManager/ImageManagerPopup.jsx
import React, { useState, useEffect } from "react";
import {
  Grid,
  Button,
  IconButton,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";
import { LuView } from "react-icons/lu";

import { RiDeleteBinLine } from "react-icons/ri";
import { MdAddPhotoAlternate } from "react-icons/md";
import { JsonForms } from "@jsonforms/react";
import {
  materialRenderers,
  materialCells,
} from "@jsonforms/material-renderers";
import { GetTableListAPI } from "../../apiCaller/getAPIsCaller";
import { useGetDestinationsImagesQuery } from "../../apiCaller/apis/getApi";
import { useDeleteDestinationImagesMutation } from "../../apiCaller/apis/deleteApi";
import { toast } from "react-toastify";
import { useAddDestinationImageMutation } from "../../apiCaller/apis/postApi";
import { useUpdateDestinationImageMutation } from "../../apiCaller/apis/updateApi";

// Placeholder for your API calls
const fetchImagesApi = async (id) => {
  // Simulate API call
  console.log(`Fetching images for ID: ${id}`);
  return new Promise((resolve) => {
    setTimeout(() => {
      const mockImages = [
        {
          id: `img-${id}-1`,
          url: "https://via.placeholder.com/150/FF0000/FFFFFF?text=Image+1",
          alt: "Image 1",
        },
        {
          id: `img-${id}-2`,
          url: "https://via.placeholder.com/150/00FF00/000000?text=Image+2",
          alt: "Image 2",
        },
        {
          id: `img-${id}-3`,
          url: "https://via.placeholder.com/150/0000FF/FFFFFF?text=Image+3",
          alt: "Image 3",
        },
      ];
      resolve(mockImages);
    }, 1000);
  });
};

const deleteImagesApi = async (imageIds) => {
  console.log("Deleting images:", imageIds);
  return new Promise((resolve) =>
    setTimeout(() => resolve({ success: true }), 500)
  );
};

const uploadImagesApi = async (rowId, files) => {
  console.log(`Uploading images for row ID ${rowId}:`, files);
  // In a real scenario, you'd use FormData to send files
  return new Promise((resolve) =>
    setTimeout(() => resolve({ success: true }), 1500)
  );
};

// JSONForms schema for image upload (simple example)
const uploadSchema = {
  type: "object",
  properties: {
    images: {
      type: "array",
      title: "Select Images to Upload",
      items: {
        type: "string", // This would typically be a file input type, but JSONForms' material renderers don't directly support file input for multiple files without custom renderers.
        // For a real file upload, you'd likely use a separate input or a custom JSONForms renderer.
      },
      options: {
        format: "url", // This is just a hint, not actual file upload. We'll handle files separately.
      },
    },
  },
};

const uploadUiSchema = {
  type: "VerticalLayout",
  elements: [
    {
      type: "Control",
      scope: "#/properties/images",
      label: "Upload New Images",
      options: {
        // You might need a custom renderer here for actual file inputs
        // For now, we'll use a hidden input and a button for simplicity outside of JSONForms.
      },
    },
  ],
};

export default function ImageManagerPopup({ rowId, close, title, subtitle }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [asCover, setAsCover] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedForDeletion, setSelectedForDeletion] = useState({});
  const [selectedAsCover, setSelectedAsCover] = useState({});
  const [filesToUpload, setFilesToUpload] = useState([]);

  const {
      data: destinationImages,
      error: destinationImagesError,
      isLoading: destinationImagesIsLoading,
      isFetching: destinationImagesIsFetching,
    } = GetTableListAPI(
      { destinationId: rowId },
      useGetDestinationsImagesQuery
    );
  
  const [
      addDestinationImage,
      {
        data: addDestinationImagenResponse,
        isLoading: uaddDestinationImageLoading,
        isError: uaddDestinationImageIsError,
        error: uaddDestinationImageError,
        isSuccess: uaddDestinationImageIsSuccess,
      },
    ] = useAddDestinationImageMutation({});
  
  const [
      deleteDestinationImages,
      {
        data: response,
        isLoading: deleteLoading,
        isError: postIsError,
        error: postError,
        isSuccess,
      },
    ] = useDeleteDestinationImagesMutation({});

  const [
      updateDestinationImage,
      {
        data: updateDestinationResponse,
        isLoading: updateDestinationLoading,
        isError: updateDestinationIsError,
        error: updateDestinationError,
        isSuccess: updateDestinationIsSuccess,
      },
    ] = useUpdateDestinationImageMutation({});

  useEffect(() => {
    fetchImages(destinationImages);
  }, [destinationImages]);


  const fetchImages = async (images) => {
    setLoading(true);
    try {
      if(images.length === 0){
        setImages([]);
        return;
      }
      setImages(images);
    } catch (error) {
      console.error("Error fetching images:", error);
      // Handle error (e.g., show a toast)
    } finally {
      setLoading(false);
    }
  };

  const toggleSelectForDeletion = (imageId) => {
    setSelectedForDeletion((prev) => ({
      ...prev,
      [imageId]: !prev[imageId],
    }));
  };

  const toggleSelectAsCover = (imageId) => {
    const coverId = Object.keys(selectedAsCover).filter(
      (id) => selectedAsCover[id]
    );
    if(coverId.length > 0 && parseInt(coverId[0]) === imageId) {
      setSelectedAsCover({});
      return;
    }
    console.log(typeof coverId);
    console.log(typeof imageId);
    console.log("Current coverId:", coverId);
    setSelectedAsCover({ [imageId]: true });
  };
  const handleDeleteSubmit = async () => {
    const idsToDelete = Object.keys(selectedForDeletion).filter(
      (id) => selectedForDeletion[id]
    );

    if (idsToDelete.length === 0) {
      alert("Please select images to delete.");
      return;
    }

    setDeleting(true);

    
    try {
      const result = await deleteDestinationImages(idsToDelete);
      if (result) {
        if (result.data.IsSuccess) {
          toast("Images deleted successful");
        } else {
          toast("Action was unsuccessful");
        }
      }
      setSelectedForDeletion({}); 
    } catch (error) {
      console.error("Error deleting images:", error);
      alert("Failed to delete images.");
    } finally {
      setDeleting(false);
    }
  };

  const handleImageUpdateSubmit = async () => {
    const ids = Object.keys(selectedAsCover).filter(
      (id) => selectedAsCover[id]
    );

    if (ids.length === 0) {
      alert("Please select image to set as cover.");
      return;
    }

    setAsCover(true);

    try {
      const result = await updateDestinationImage({imageId:ids[0], data:{ IsDisplayImage: true }});
      if (result) {
        if (result.data.IsSuccess) {
          toast("Images updates successful");
        } else {
          toast("Action was unsuccessful");
        }
      }
      setSelectedForDeletion({});
    } catch (error) {
      console.error("Error updating images:", error);
      alert("Failed to delete images.");
    } finally {
      setAsCover(false);
    }
  };


  const handleFileChange = (event) => {
    const maxSize = 2 * 1024 * 1024; // 2 MB
    const files = Array.from(event.target.files);

    const validFiles = files.filter((file) => {
      if (file.size > maxSize) {
        alert(`${file.name} is too large! Maximum size is 2MB.`);
        return false;
      }
      return true;
    });

    setFilesToUpload(validFiles);
  };
  

  const handleUploadSubmit = async () => {
    if (filesToUpload.length === 0) {
      alert("Please select files to upload.");
      return;
    }
    console.log(filesToUpload);

    setUploading(true);
    try {
      const result = await addDestinationImage({destinationId:rowId, images:filesToUpload});
      if (result) {
        console.log(result?.data?.IsSuccess);
        if (
          result?.data?.IsSuccess !== undefined &&
          result?.data?.IsSuccess !== false
        ) {
          toast("Destination Image Added successful");
        } else {
          toast("An error occured");
        }
      }
      setFilesToUpload([]); 
    } catch (error) {
      console.error("Error uploading images:", error);
      alert("Failed to upload images.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-5">
      <div className="text-3xl font-bold mb-5 uppercase py-5 border-b-2 border-color-primary-dark2">
        Manage Destination Images
      </div>
      <p className="text-gray-600 mb-10 text-3xl">
        For Destination: <span className="font-bold">{title}</span>
      </p>

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="200px"
        >
          <CircularProgress />
          <Typography variant="h6" className="ml-3">
            Loading images...
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2} className="mb-6">
          {images.length === 0 ? (
            <Grid item xs={12}>
              <Typography variant="body1">
                No images found for this item.
              </Typography>
            </Grid>
          ) : (
            images.map((image) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={image.id}>
                <Box
                  className={`relative border-2 ${
                    selectedForDeletion[image.id]
                      ? "border-red-500"
                      : "border-gray-200"
                  } rounded-lg overflow-hidden transition-all`}
                >
                  <img
                    src={image.image}
                    alt={image.caption || "Image"}
                    className="w-full h-40 object-cover"
                  />
                  <IconButton
                    className="absolute top-1 right-1 bg-white bg-opacity-75 hover:bg-opacity-100 p-1 rounded-full"
                    onClick={() => toggleSelectForDeletion(image.id)}
                    color={selectedForDeletion[image.id] ? "error" : "default"}
                    size="small"
                  >
                    <RiDeleteBinLine size={20} />
                  </IconButton>
                  <IconButton
                    className="absolute top-1 right-1 bg-white bg-opacity-75 hover:bg-opacity-100 p-1 rounded-full"
                    onClick={() => toggleSelectAsCover(image.id)}
                    color={selectedAsCover[image.id] ? "success" : "default"}
                    size="small"
                  >
                    <LuView size={20} />
                  </IconButton>

                  {selectedForDeletion[image.id] && (
                    <Box
                      className="absolute inset-0 bg-red-500 bg-opacity-30 flex items-center justify-center"
                      sx={{ pointerEvents: "none" }} // Prevent clicks from interfering with the image
                    >
                      <Typography variant="button" color="white">
                        Marked for Deletion
                      </Typography>
                    </Box>
                  )}

                  {selectedAsCover[image.id] && (
                    <Box
                      className="absolute inset-0 bg-green-500 bg-opacity-30 flex items-center justify-center"
                      sx={{ pointerEvents: "none" }} // Prevent clicks from interfering with the image
                    >
                      <Typography variant="button" color="white">
                        Marked As Cover
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            ))
          )}
        </Grid>
      )}

      <Box className="flex space-x-5 items-center mt-4 mb-6">
        <Button
          variant="contained"
          color="error"
          onClick={handleDeleteSubmit}
          disabled={
            deleting ||
            Object.keys(selectedForDeletion).filter(
              (id) => selectedForDeletion[id]
            ).length === 0
          }
          startIcon={
            deleting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <RiDeleteBinLine />
            )
          }
        >
          {deleting ? "Deleting..." : "Delete Selected Images"}
        </Button>

        <Button
          variant="contained"
          color="success"
          onClick={handleImageUpdateSubmit}
          disabled={
            asCover ||
            Object.keys(selectedAsCover).filter((id) => selectedAsCover[id])
              .length === 0
          }
          startIcon={
            asCover ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <LuView />
            )
          }
        >
          {asCover ? "Setting as Cover..." : "Set Selected Image as cover"}
        </Button>
      </Box>

      <hr className="my-6" />

      <h3 className="text-xl font-bold mb-3">Upload New Images</h3>
      <Box className="mb-4">
        {/* Using a direct file input for multi-file upload, as JSONForms needs custom renderers for this */}
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileChange}
          id="image-upload-input"
          className="hidden"
        />
        <label htmlFor="image-upload-input">
          <Button
            variant="outlined"
            component="span"
            startIcon={<MdAddPhotoAlternate />}
          >
            Select Files
          </Button>
        </label>
        {filesToUpload.length > 0 && (
          <Typography variant="body2" className="ml-3 inline-block">
            {filesToUpload.length} file(s) selected
          </Typography>
        )}
      </Box>

      <Box>
        <Button
          variant="contained"
          color="primary"
          onClick={handleUploadSubmit}
          disabled={uploading || filesToUpload.length === 0}
          startIcon={
            uploading ? <CircularProgress size={20} color="inherit" /> : null
          }
        >
          {uploading ? "Uploading..." : "Upload Images"}
        </Button>
        <Button variant="outlined" onClick={close} className="ml-3">
          Close
        </Button>
      </Box>
    </div>
  );
}
